import { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Download, Share, Plus } from "lucide-react";

function isIosSafari() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent;
  const isIos = /iphone|ipad|ipod/i.test(ua);
  // Safari on iOS (exclude Chrome/Firefox on iOS which use CriOS/FxiOS)
  const isSafari = /safari/i.test(ua) && !/crios|fxios|edgios/i.test(ua);
  return isIos && isSafari;
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

// className/variant let callers match nav vs sidebar styling.
export default function InstallButton({ className = "", variant = "outline", label = "Install App", testid = "install-app-btn" }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [ios, setIos] = useState(false);
  const [showIosModal, setShowIosModal] = useState(false);
  const [standalone, setStandalone] = useState(false);

  useEffect(() => {
    setStandalone(isStandalone());
    setIos(isIosSafari());

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setDeferredPrompt(null);
      setStandalone(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // Already installed -> never show.
  if (standalone) return null;

  // Show only when installable (Chrome/Edge/Android fired the event) or on iOS Safari (manual instructions).
  const canShow = !!deferredPrompt || ios;
  if (!canShow) return null;

  const handleClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setDeferredPrompt(null);
    } else if (ios) {
      setShowIosModal(true);
    }
  };

  return (
    <>
      <Button data-testid={testid} variant={variant} onClick={handleClick} className={className}>
        <Download className="mr-2 h-4 w-4" /> {label}
      </Button>

      <Dialog open={showIosModal} onOpenChange={setShowIosModal}>
        <DialogContent className="max-w-sm" data-testid="ios-install-modal">
          <DialogHeader>
            <DialogTitle className="font-heading">Install Fast Estimate</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-slate-600">To install Fast Estimate on your iPhone:</p>
            <div className="flex items-center gap-3 rounded-xl border-2 border-slate-200 p-4">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-indigo-50">
                <Share className="h-5 w-5 text-indigo-500" />
              </div>
              <p className="text-sm text-slate-700">1. Tap the <span className="font-bold">Share</span> icon in Safari's toolbar.</p>
            </div>
            <div className="flex items-center gap-3 rounded-xl border-2 border-slate-200 p-4">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-indigo-50">
                <Plus className="h-5 w-5 text-indigo-500" />
              </div>
              <p className="text-sm text-slate-700">2. Scroll down and tap <span className="font-bold">"Add to Home Screen."</span></p>
            </div>
            <Button data-testid="ios-modal-close" onClick={() => setShowIosModal(false)} className="h-12 w-full rounded-xl bg-indigo-500 font-bold hover:bg-indigo-600">Got it</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
